import { computed, ComputedRef } from "@vue/composition-api"
import { Dictionary } from "vue-router/types/router"

import {
  Widget,
  WidgetCheckbox,
  WidgetComboBox,
  WidgetRadio,
  WidgetSlider,
  WidgetText,
  WidgetTextIterable,
  WizardConfigItem,
  WizardRule
} from "@/types/wizard"

export const useWizard = (configuration: ComputedRef<WizardConfigItem[]>) => {
  const resourceKeyMap = computed<Dictionary<WizardConfigItem<Widget>>>(() => {
    return configuration.value.reduce((map: Dictionary<WizardConfigItem<Widget>>, item) => {
      if (item.resourceKey) map[item.resourceKey] = item
      return map
    }, {})
  })

  /**
   * Validates any widget in the wizard configuration.
   * @param resourceKey The resource key of the widget to validate.
   */
  const validate = (resourceKey: string) => {
    if (resourceKeyMap.value[resourceKey]) {
      const widget = resourceKeyMap.value[resourceKey]
      const isActiveWidget = widget.settings.active
      const isRequiredWidget = widget.settings.required

      if (!isActiveWidget) return true

      if (widget.type === "text") {
        return _validateTextWidget(widget, isRequiredWidget)
      } else if (widget.type === "iterableText") {
        return _validateIterableTextWidget(widget, isRequiredWidget)
      } else if (widget.type === "checkbox") {
        return _validateCheckboxWidget(widget, isRequiredWidget)
      } else if (widget.type === "radio") {
        return _validateRadioWidget(widget, isRequiredWidget)
      } else if (widget.type === "comboBox") {
        return _validateComboBoxWidget(widget, isRequiredWidget)
      } else if (widget.type === "slider") {
        return _validateSliderWidget(widget, isRequiredWidget)
      } else {
        _logTypeError(`Unknown widget type "${widget.type}"`)
        return null
      }
    }

    _logTypeError(`resourceKey "${resourceKey}" not found`)

    return null
  }

  const _validateIterableTextWidget = (widget: WizardConfigItem<Widget>, required: boolean) => {
    if (widget.type === "iterableText") {
      const iterableTextWidget = widget.settings.widget as WidgetTextIterable
      const { rule, options } = iterableTextWidget

      if (rule) {
        return options.every(option => {
          const { value1, value2 } = option
          const { value1: ruleValue1, value2: ruleValue2 } = rule

          return _validateWidget(ruleValue1, value1) && _validateWidget(ruleValue2, value2)
        })
      } else if (required) {
        return options.every(option => {
          return Boolean(option.value1) && Boolean(option.value2)
        })
      }

      return true
    } else {
      _logTypeError(`Expected type "iterableText" but got "${widget.type}"`)
    }
  }

  const _validateSliderWidget = (widget: WizardConfigItem<Widget>, required: boolean) => {
    const sliderWidget = widget.settings.widget as WidgetSlider
    const { value } = sliderWidget

    if (required) {
      return Boolean(value)
    }

    return true
  }

  const _validateComboBoxWidget = (widget: WizardConfigItem<Widget>, required: boolean) => {
    const comboBoxWidget = widget.settings.widget as WidgetComboBox
    const { value } = comboBoxWidget

    if (required) {
      return Boolean(value)
    }

    return true
  }

  const _validateRadioWidget = (widget: WizardConfigItem<Widget>, required: boolean) => {
    const radioWidget = widget.settings.widget as WidgetRadio
    const { value } = radioWidget

    if (required) {
      return Boolean(value)
    }

    return true
  }

  const _validateCheckboxWidget = (widget: WizardConfigItem<Widget>, required: boolean) => {
    const checkboxWidget = widget.settings.widget as WidgetCheckbox
    const { options } = checkboxWidget

    if (required) {
      return options.every(o => o.selected)
    }

    return true
  }

  const _validateTextWidget = (widget: WizardConfigItem<Widget>, required: boolean) => {
    if (widget.type === "text") {
      const widgetText = widget.settings.widget as WidgetText

      if (widgetText.rule) {
        return _validateWidget(widgetText.rule, widgetText.value)
      } else if (required) {
        return Boolean(widgetText.value)
      }
    } else {
      _logTypeError(`Expected type "text" but got "${widget.type}"`)
    }
  }

  const _validateWidget = (rule: WizardRule, value: string | null) => {
    if (value === null) return false

    if (rule.regex) {
      const regex = new RegExp(rule.regex)
      return regex.test(value)
    } else {
      _logTypeError("Rule is null")
      return false
    }
  }

  const _validateConfiguration = () => {
    const config = configuration.value
    const expectedKeys = ["type", "settings", "resourceKey"]

    return config.every(c => {
      const containsWidget = "widget" in c.settings
      const validKeys = Object.keys(c).some(key => {
        return expectedKeys.includes(key)
      })

      return validKeys && containsWidget
    })
  }

  const _logTypeError = (message: string) => {
    const errorMessage = `[Composable useWizard]: ${message}`
    throw new TypeError(errorMessage)
  }

  const _init = () => {
    if (!_validateConfiguration()) {
      _logTypeError("User configuration is not valid")
    }
  }
  _init()

  return {
    wizard: configuration,
    validate
  }
}
